import React, { useState, useEffect } from 'react'
import axios from 'axios'
import {
  DatePicker,
  Select,
  Space,
  Table,
  Tag,
  Typography
} from 'antd'

import advantageMaterials from '../data/advantage/materials'
import integrityMaterials from '../data/integrity/materials'
import advantageParts from '../data/advantage/parts'
import integrityParts from '../data/integrity/parts'

const { Title } = Typography
const { Option } = Select
const { RangePicker } = DatePicker

// Build a lookup: itemId → { partNumber, description } across all datasets
const ITEM_LOOKUP = {}
;[...advantageMaterials, ...integrityMaterials, ...advantageParts, ...integrityParts].forEach(item => {
  if (!ITEM_LOOKUP[item.id]) {
    ITEM_LOOKUP[item.id] = { partNumber: item.partNumber || null, description: item.description || null }
  }
})

function resolveItemId(itemId) {
  if (itemId === null || itemId === undefined) return '—'
  const entry = ITEM_LOOKUP[itemId]
  if (!entry) return itemId
  return entry.partNumber || entry.description || itemId
}

const FIELD_LABELS = {
  'material.pricePerPound': 'Price Per Pound',
  'material.pricePerUnit': 'Price Per Unit',
  'part.fastener': 'Part Fastener',
  'part.cover': 'Part Cover',
  'formula.galvRate': 'Galvanization Rate',
  'formula.overhead': 'Overhead Factor',
  'formula.margin': 'Margin Factor',
  'formula.markup': 'Additional Markup',
  'formula.fee105': 'Freight Fee'
}

const columns = [
  {
    title: 'When',
    dataIndex: 'when',
    key: 'when',
    render: val => new Date(val).toLocaleString(),
    sorter: (a, b) => new Date(b.when) - new Date(a.when),
    defaultSortOrder: 'ascend'
  },
  {
    title: 'User',
    dataIndex: 'whoName',
    key: 'whoName'
  },
  {
    title: 'Building Type',
    dataIndex: 'buildingType',
    key: 'buildingType',
    render: val => <Tag>{val}</Tag>
  },
  {
    title: 'Field',
    key: 'field',
    render: (_, row) => (
      <span>
        {FIELD_LABELS[row.field] ?? row.field}
        {row.isRollback && <Tag color="orange" style={{ marginLeft: 8 }}>Rollback</Tag>}
      </span>
    )
  },
  {
    title: 'Part # / Description',
    dataIndex: 'itemId',
    key: 'itemId',
    render: val => resolveItemId(val)
  },
  {
    title: 'Old Value',
    dataIndex: 'oldValue',
    key: 'oldValue',
    render: val => val ?? '—'
  },
  {
    title: 'New Value',
    dataIndex: 'newValue',
    key: 'newValue',
    render: val => val ?? '—'
  }
]

export default function PriceChangeLog() {
  const [logs, setLogs] = useState([])
  const [allLogs, setAllLogs] = useState([])
  const [loading, setLoading] = useState(false)
  const [buildingType, setBuildingType] = useState('all')
  const [fieldFilter, setFieldFilter] = useState(null)
  const [userFilter, setUserFilter] = useState(null)
  const [dateRange, setDateRange] = useState(null)

  useEffect(() => {
    setLoading(true)
    axios.get(`/api/pricing/log/${buildingType}`, {
      params: { limit: 500 },
      withCredentials: true
    })
      .then(res => setAllLogs(res.data))
      .catch(() => setAllLogs([]))
      .finally(() => setLoading(false))
  }, [buildingType])

  // Apply client-side filters
  useEffect(() => {
    let filtered = allLogs
    if (fieldFilter) filtered = filtered.filter(l => l.field === fieldFilter)
    if (userFilter) filtered = filtered.filter(l => l.whoName === userFilter)
    if (dateRange?.[0] && dateRange?.[1]) {
      const from = dateRange[0].startOf('day').valueOf()
      const to = dateRange[1].endOf('day').valueOf()
      filtered = filtered.filter(l => {
        const t = new Date(l.when).valueOf()
        return t >= from && t <= to
      })
    }
    setLogs(filtered)
  }, [allLogs, fieldFilter, userFilter, dateRange])

  // Derive unique users from loaded logs
  const users = [...new Set(allLogs.map(l => l.whoName).filter(Boolean))]

  return (
    <>
      <Title level={2}>Pricing Change Log</Title>

      <Space wrap style={{ marginBottom: 16 }}>
        <Select value={buildingType} onChange={setBuildingType} style={{ width: 180 }}>
          <Option value="all">All Building Types</Option>
          <Option value="advantage">Advantage</Option>
          <Option value="integrity">Integrity</Option>
          <Option value="global">Global (Formula)</Option>
        </Select>

        <Select
          allowClear
          placeholder="Filter by field"
          value={fieldFilter}
          onChange={setFieldFilter}
          style={{ width: 180 }}
        >
          {Object.entries(FIELD_LABELS).map(([key, label]) => (
            <Option key={key} value={key}>{label}</Option>
          ))}
        </Select>

        <Select
          allowClear
          placeholder="Filter by user"
          value={userFilter}
          onChange={setUserFilter}
          style={{ width: 180 }}
        >
          {users.map(u => <Option key={u} value={u}>{u}</Option>)}
        </Select>

        <RangePicker
          value={dateRange}
          onChange={setDateRange}
          allowClear
        />

        {(buildingType !== 'all' || fieldFilter || userFilter || dateRange) && (
          <a
            onClick={() => {
              setBuildingType('all')
              setFieldFilter(null)
              setUserFilter(null)
              setDateRange(null)
            }}
            style={{ whiteSpace: 'nowrap' }}
          >
            Clear filters
          </a>
        )}
      </Space>

      <Table
        columns={columns}
        dataSource={logs}
        rowKey="_id"
        loading={loading}
        pagination={{ pageSize: 50 }}
        size="small"
      />
    </>
  )
}
